import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, ArrowRight, Layers } from 'lucide-react';
import { getDeadlineFlag, DEADLINE_STYLES } from '../utils/deadlineFlag';

const STAGE_LABELS = {
  new: 'New', casting: 'Casting', stone_setting: 'Stone Setting',
  polishing: 'Polishing', rhodium: 'Rhodium', final_qc: 'Final QC',
};

const fmtHours = (h) => {
  if (!h || h === 0) return '-';
  if (h < 1) return '<1h';
  if (h < 24) return `${Math.round(h)}h`;
  const days = Math.floor(h / 24);
  const hrs = Math.round(h % 24);
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
};

const DeptLoadBoard = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState([]);
  const [summary, setSummary] = useState({});
  const [bottleneck, setBottleneck] = useState(null);
  const [loading, setLoading] = useState(true);

  // Drill-down state
  const [drillStage, setDrillStage] = useState(null);
  const [drillOrders, setDrillOrders] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [workerFilter, setWorkerFilter] = useState('all');

  const fetchBoard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/department-load-board');
      setBoard(res.data.board);
      setSummary(res.data.summary);
      setBottleneck(res.data.bottleneck);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBoard(); }, []);

  const drillInto = async (stage) => {
    setDrillStage(stage);
    setDrillLoading(true);
    setWorkerFilter('all');
    try {
      const res = await api.get(`/department-load-board/${stage}/orders`);
      setDrillOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setDrillLoading(false); }
  };

  const goBack = () => {
    setDrillStage(null);
    setDrillOrders([]);
  };

  // Workers in drill-down
  const drillWorkers = [...new Set(drillOrders.map(o => o.assigned_worker_name).filter(Boolean))].sort();
  const filteredDrillOrders = workerFilter === 'all'
    ? drillOrders
    : drillOrders.filter(o => o.assigned_worker_name === workerFilter);

  // ===== DRILL-DOWN VIEW =====
  if (drillStage) {
    return (
      <div className="h-screen flex flex-col bg-gray-50" data-testid="drill-down-view">
        <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={goBack}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold">{STAGE_LABELS[drillStage] || drillStage}</h1>
                <p className="text-xs text-emerald-200">{drillOrders.length} orders at this stage</p>
              </div>
            </div>
          </div>
        </div>

        {/* Worker Filter */}
        {drillWorkers.length > 0 && (
          <div className="px-4 pt-3">
            <Select value={workerFilter} onValueChange={setWorkerFilter}>
              <SelectTrigger className="h-9 w-48">
                <SelectValue placeholder="All Workers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workers ({drillOrders.length})</SelectItem>
                {drillWorkers.map(w => (
                  <SelectItem key={w} value={w}>{w} ({drillOrders.filter(o => o.assigned_worker_name === w).length})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-2">
          {drillLoading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : filteredDrillOrders.length === 0 ? (
            <p className="text-center py-8 text-gray-400">No orders at this stage</p>
          ) : filteredDrillOrders.map(order => {
            const c = order._computed || {};
            const flag = getDeadlineFlag(order);
            return (
              <div key={order._id}
                onClick={() => navigate('/messenger', { state: { selectOrder: order._id } })}
                className={`rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow ${
                  c.is_overdue ? 'bg-red-50 border-red-200' : c.is_at_risk ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
                }`}
                data-testid={`drill-order-${order.order_number}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{order.order_number}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${DEADLINE_STYLES[flag.level]}`}>{flag.label}</span>
                      {c.is_at_risk && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">AT RISK</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{order.title} {order.main_group ? `— ${order.main_group}` : ''}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{fmtHours(c.hours_in_stage)}</span>
                      {order.committed_delivery_date && <span>{order.committed_delivery_date}</span>}
                      {order.rework_count > 0 && <span className="text-amber-600 font-bold">↻ {order.rework_count}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2 text-xs">
                    {order.assigned_worker_name && <p className="text-gray-500">{order.assigned_worker_name}</p>}
                    {order.order_owner_name && <p className="text-emerald-600">{order.order_owner_name}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ===== BOARD VIEW =====
  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="dept-load-board-page">
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2"><Layers className="w-5 h-5" /> Department Load</h1>
              <p className="text-xs text-emerald-200">{summary.total_orders || 0} active orders across stages</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchBoard}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="px-4 pt-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-emerald-700">{summary.total_orders || 0}</p>
            <p className="text-[10px] text-emerald-600 font-semibold uppercase">Active</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-red-700">{summary.total_overdue || 0}</p>
            <p className="text-[10px] text-red-600 font-semibold uppercase">Overdue</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-amber-700">{summary.total_at_risk || 0}</p>
            <p className="text-[10px] text-amber-600 font-semibold uppercase">At Risk</p>
          </div>
        </div>
        {bottleneck && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Bottleneck: <span className="font-bold">{STAGE_LABELS[bottleneck] || bottleneck}</span></span>
          </div>
        )}
      </div>

      {/* Board */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-2">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : board.map(row => {
          const isBn = row.stage === bottleneck;
          const hasIssues = row.overdue > 0 || row.at_risk > 0;
          return (
            <div key={row.stage}
              onClick={() => row.count > 0 && drillInto(row.stage)}
              className={`rounded-lg border p-3 transition-shadow ${
                row.count > 0 ? 'cursor-pointer hover:shadow-md' : 'opacity-50'
              } ${isBn ? 'border-red-400 bg-red-50 ring-1 ring-red-300' : hasIssues ? 'bg-amber-50/50 border-amber-200' : 'bg-white border-gray-200'}`}
              data-testid={`stage-row-${row.stage}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${
                    isBn ? 'bg-red-200 text-red-700' : row.count > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {row.count}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{STAGE_LABELS[row.stage] || row.stage}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>Avg: {fmtHours(row.avg_hours)}</span>
                      <span>Oldest: {fmtHours(row.oldest_hours)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {row.overdue > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">{row.overdue} overdue</span>
                  )}
                  {row.at_risk > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">{row.at_risk} at risk</span>
                  )}
                  {row.count > 0 && <ArrowRight className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeptLoadBoard;
